import { Component, DestroyRef, inject, Input } from '@angular/core';
import { createUserDetailsForm, Party, PartyFormGroup } from '../../models/user-details-form';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, tap } from 'rxjs';
import { openConfirmationDialog } from '../../../../../../../../components/confirmation-popup/confirmation-popup';
import { PartySelectorComponent } from '../party-selector/party-selector.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { PartyColumnFilterComponent } from '../party-column-filter/party-column-filter.component';

type PartyFilterColumn = 'name' | 'number';

@Component({
  selector: 'app-parties-table',
  standalone: true,
  templateUrl: './parties-table.component.html',
  imports: [
    MatIcon,
    MatIconButton,
    PartySelectorComponent,
    MatCheckbox,
    ReactiveFormsModule,
    MatSortHeader,
    MatSort,
    PartyColumnFilterComponent
  ],
  styleUrl: './parties-table.component.scss'
})
export class PartiesTableComponent {
  @Input() group = createUserDetailsForm();
  @Input() controlName = '';
  @Input() checkboxControlName = '';
  @Input() checkboxLabel = '';

  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private filters: Partial<Record<PartyFilterColumn, string>> = {};
  private mainPartyCache: Party | null = null;

  partyControls() {
    return this.partiesArray().controls.filter(control => {
      const party = control.getRawValue();

      return Object.entries(this.filters).every(([column, filterValue]) => {
        if (!filterValue) {
          return true;
        }

        const partyValue = party[column as PartyFilterColumn];

        return String(partyValue ?? '')
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      });
    });
  }

  removeParty(party: PartyFormGroup) {
    openConfirmationDialog(this.dialog, {
      title: 'Remove party',
      message: 'Are you sure you want to remove this party?'
    })
      .afterClosed()
      .pipe(
        filter(result => result === 'Confirmed'),
        tap(() => {
          const index = this.partiesArray().controls.indexOf(party);

          if (index !== -1) {
            this.partiesArray().removeAt(index);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  mainParty(): Party | null {
    const number = this.group.get('mainPartyNumber')?.value;
    const name = this.group.get('mainPartyName')?.value;

    if (!number || !name) {
      this.mainPartyCache = null;
      return null;
    }

    // Returns the same object reference across change-detection cycles unless the
    // main party actually changed - app-party-selector's suggestedParty is a signal
    // input, and a fresh object literal on every tick would look like a change on
    // every tick, endlessly re-triggering its internal validity/search pipeline.
    if (this.mainPartyCache?.number !== number || this.mainPartyCache?.name !== name) {
      this.mainPartyCache = { number, name };
    }

    return this.mainPartyCache;
  }

  onPartySelect(party: Party | null) {
    if (!party) {
      return;
    }

    const alreadyExists = this.partiesArray()
      .getRawValue()
      .find(({ number }) => party.number === number);

    if (!alreadyExists) {
      const newPartyGroup: PartyFormGroup = new FormGroup({
        number: new FormControl(party.number, { nonNullable: true }),
        name: new FormControl(party.name, { nonNullable: true })
      });
      this.partiesArray().push(newPartyGroup);
    }
  }

  onSort({ direction, active }: Sort) {
    const partiesArray = this.partiesArray();

    if (!direction || !active) {
      return;
    }

    const sortedControls = [...partiesArray.controls].sort((a, b) => {
      const aValue = a.get(active)?.value;
      const bValue = b.get(active)?.value;

      if (active === 'number') {
        return this.compareNumbers(Number(aValue), Number(bValue), direction);
      }

      return this.compareStrings(String(aValue ?? ''), String(bValue ?? ''), direction);
    });

    sortedControls.forEach((control, index) => {
      partiesArray.setControl(index, control);
    });
  }

  onFilter(value: string | null, column: PartyFilterColumn) {
    const normalizedValue = value?.trim().toLowerCase();

    if (!normalizedValue) {
      delete this.filters[column];
      return;
    }

    this.filters[column] = normalizedValue;
  }

  private compareStrings(a: string, b: string, direction: 'asc' | 'desc') {
    const result = a.localeCompare(b, undefined, { sensitivity: 'base' });
    return direction === 'asc' ? result : -result;
  }

  private compareNumbers(a: number, b: number, direction: 'asc' | 'desc') {
    const result = a - b;
    return direction === 'asc' ? result : -result;
  }

  private partiesArray() {
    return this.group.get(this.controlName) as FormArray<PartyFormGroup>;
  }
}
